import TakisEjecucion from '../../components/TakisEjecucion';
import { faltaTakis } from '../../domain/takisCierre';
import { useMemo, useState } from 'react';
import {
  EXHIBICION, INCIDENCIA, OBJECION_NINGUNA, QUIEBRE,
  competenciaDeLaOficina, cuestionarioDe, loQueFaltaEnCierre, objecionesDeLaOficina, totalUnidades,
} from '../../domain/impulso';
import type { CierreDeTurno, ObjecionElegida, VentaSku } from '../../domain/impulso';
import { corregirCierre, registrarCierreManual } from '../../api/impulso';
import type { CambiosDeCierre, FilaDeCierre, SkuDeImpulso } from '../../api/impulso';
import { ESTADO_DE_CIERRE_LABEL } from '../../domain/impulso';

/**
 * Lo que el sistema cree de este turno, cuando eso no es «trabajo y no dejo su cierre».
 *
 * El usuario pidio poder agregar registro tambien en una ausencia y en un turno que no encaja en el
 * caso normal. La accion no se bloquea —el coordinador sabe lo que paso y el sistema no—, pero
 * quien completa tiene que ver con que esta en desacuerdo antes de escribir. Sin este aviso,
 * registrar sobre una ausencia parece un dato mas y despues nadie entiende de donde salio.
 */
const AVISO_POR_ESTADO: Partial<Record<string, string>> = {
  ausencia: 'Este turno tiene una falta justificada aprobada para esa fecha. Si la persona '
    + 'igual trabajó, o el permiso era de medio día, registra el cierre y explícalo en el motivo.',
  en_curso: 'El turno todavía no terminó según las marcas. Si ya salió y te pasó sus números, '
    + 'regístralo; el sistema no lo va a contradecir.',
  futuro: 'Este turno es de una fecha que todavía no llegó. Revisa la fecha antes de registrar: '
    + 'un cierre acá entra al tablero del cliente como jornada trabajada.',
  sin_marca: 'No hay marca de ingreso ni de salida en este turno. Si trabajó y olvidó marcar, el '
    + 'cierre es el registro que lo rescata — pero la asistencia sigue faltando y se corrige aparte.',
};

/**
 * La ventana para COMPLETAR o EDITAR un cierre de turno desde el panel.
 *
 * Pedido del usuario del 8-set: *«necesito urgente esa ventana para editar y registrar
 * manualmente»*, con el formato de la bandeja de validación de turnos.
 *
 * ---------------------------------------------------------------------------
 * DOS DESVÍOS DEL DISEÑO APROBADO, los dos a propósito
 * ---------------------------------------------------------------------------
 *
 * **1. No comparte el JSX con el formulario del celular.** El spec proponía extraer el cuerpo de
 * preguntas de `movil/CierreDeTurnoForm.tsx` a un componente común. Al mirarlo de cerca, lo que
 * había que compartir YA está compartido: las dos pantallas ITERAN los mismos catálogos de
 * `domain/impulso.ts` (`QUIEBRE`, `EXHIBICION`, `COMPETENCIA`, `OBJECION`, `INCIDENCIA`) y el mismo
 * `impulso_skus` de la campaña, y las dos validan con `loQueFaltaEnCierre`. Una objeción nueva o un
 * SKU nuevo aparece solo en ambas sin compartir una línea de JSX.
 *
 * Lo que NO debería compartirse es justamente el layout: el del celular es una pila de tarjetas de
 * una pregunta cada una, con blancos grandes, pensada para el pulgar a las 10 de la noche; esta es
 * una grilla densa para alguien con mouse que va a llenar varias seguidas. Forzar el mismo JSX
 * habría hecho peor las dos pantallas para evitar una duplicación que no existe.
 *
 * ⚠️ Sí encontré una duplicación real, y NO está acá: `movil/CierreDeTurnoForm.tsx:324` tiene las
 * líneas de producto CABLEADAS (`['Rapiditas', 'Tostadas']`). Hoy coincide con los datos por
 * casualidad; una línea nueva en `impulso_skus` desaparecería de la app de la impulsadora sin
 * ningún error. Esta pantalla las deriva de los datos, y lo del celular quedó en `PENDIENTES.md`.
 *
 * **2. Editar calcula el diff al guardar, en lugar de casillas «cambiar este campo».** El spec
 * copiaba el patrón del modal de corrección de `Validacion.tsx`. Ahí las casillas hacen falta por
 * una razón concreta: uno de los campos es una FOTO, y un input de archivo no tiene un estado
 * «igual que antes» — sin la casilla no se puede distinguir «no la toqué» de «la borré».
 *
 * Acá todos los campos son números, textos y opciones que se pre-cargan con su valor actual, así
 * que «no lo toqué» ya significa «no cambió». El diff se calcula comparando contra el valor de
 * partida y solo viaja lo que de verdad difiere — la misma garantía que dan las casillas, con la
 * mitad de los clics. Y la RPC igual ignora un campo mandado con su mismo valor, así que el rastro
 * no puede llenarse de filas que no corrigen nada.
 */
export default function CierreDeTurnoModal({ fila, skus, marca, cliente, onCerrar, onGuardado }: {
  fila: FilaDeCierre;
  skus: SkuDeImpulso[];
  /** La marca de la campaña de la bandeja. Decide qué objeciones se ofrecen (Takis tiene las suyas). */
  marca?: string | null;
  /** El cliente de la campaña: P&G tiene su propio cuestionario (28-oct). */
  cliente?: string | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const editando = fila.cierre !== null;
  const esTakis = /takis/i.test(marca ?? '');
  const cuestionario = cuestionarioDe(false, marca, cliente);

  /** El estado de partida. En modo edición es lo que hay guardado; en alta, los valores por defecto. */
  const inicial = useMemo<CierreDeTurno>(() => hidratar(fila, skus), [fila, skus]);
  /** Las de esta campaña, más las que ya estaban guardadas para que se puedan desmarcar. */
  const objeciones = useMemo(
    () => objecionesDeLaOficina(marca, inicial.objeciones.map((o) => o.valor), cliente),
    [marca, cliente, inicial],
  );
  /** Takis y P&G sin «Unión»; un valor ya guardado se muestra igual para poder corregirlo. */
  const competencia = useMemo(
    () => competenciaDeLaOficina(marca, inicial.competencia_impulsadora, cliente),
    [marca, cliente, inicial],
  );
  const [form, setForm] = useState<CierreDeTurno>(inicial);
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  /** Las líneas de producto salen de los DATOS y no de una lista escrita a mano. */
  const lineas = useMemo(() => [...new Set(skus.map((s) => s.linea))], [skus]);

  const set = <K extends keyof CierreDeTurno>(campo: K, valor: CierreDeTurno[K]) =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const setUnidades = (code: string, texto: string) => {
    const n = texto.trim() === '' ? 0 : Number(texto);
    setForm((f) => ({
      ...f,
      ventas: f.ventas.map((v) => (v.sku_code === code ? { ...v, unidades: n } : v)),
    }));
  };

  const marcarObjecion = (valor: string) => {
    setForm((f) => {
      const ya = f.objeciones.some((o) => o.valor === valor);
      if (ya) return { ...f, objeciones: f.objeciones.filter((o) => o.valor !== valor) };
      // «Ninguna en particular» no convive con las demás: marcarla apaga todo lo otro.
      if (valor === OBJECION_NINGUNA) return { ...f, objeciones: [{ valor, comentario: '' }] };
      return {
        ...f,
        objeciones: [...f.objeciones.filter((o) => o.valor !== OBJECION_NINGUNA),
          { valor, comentario: '' }],
      };
    });
  };

  const comentarioObjecion = (valor: string, comentario: string) =>
    setForm((f) => ({
      ...f,
      objeciones: f.objeciones.map((o) => (o.valor === valor ? { ...o, comentario } : o)),
    }));

  // `turnoYaElegido`: el turno lo eligio quien hizo clic en la fila, y la RPC saca el PDV y el
  // recurso de la asignacion. Sin esta opcion el validador del celular bloquea el formulario
  // pidiendo dos campos que esta pantalla no necesita.
  const falta = loQueFaltaEnCierre(form, { turnoYaElegido: true })
    ?? (esTakis ? faltaTakis(form.ejecucion_comentario, !editando) : null);
  const cambios = editando ? diff(inicial, form) : null;
  const sinCambios = cambios !== null && Object.keys(cambios).length === 0;

  const guardar = async () => {
    setError('');
    if (falta) { setError(falta); return; }
    if (motivo.trim() === '') {
      setError(editando
        ? 'Escribe el motivo de la corrección: es lo que explica el cambio a quien lo lea después.'
        : 'Escribe el motivo: es lo único que explica por qué no lo llenó el campo.');
      return;
    }
    if (sinCambios) { setError('No cambiaste ningún dato.'); return; }
    setGuardando(true);
    try {
      if (editando && fila.cierre) {
        await corregirCierre(fila.cierre.id, cambios ?? {}, motivo.trim());
      } else if (fila.assignmentId) {
        await registrarCierreManual(fila.assignmentId, form, motivo.trim());
      } else {
        throw new Error('Este turno no tiene asignación: no hay a nombre de quién registrar.');
      }
      onGuardado();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-4 w-full max-w-3xl rounded-lg bg-white shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900">
              {editando ? 'Editar el cierre de turno' : 'Completar el cierre de turno'}
            </h2>
            <p className="truncate text-sm text-gray-600">
              {fila.trabajador} · {fila.tienda} · {fila.fecha} · {fila.horario}
            </p>
          </div>
          <button
            type="button" onClick={onCerrar}
            className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            Cerrar
          </button>
        </header>

        <div className="space-y-5 px-5 py-4">
          {/* El aviso va ARRIBA de todo: es lo que cambia como se lee el resto del formulario. */}
          {!editando && AVISO_POR_ESTADO[fila.estado] && (
            <div className="rounded border border-blue-200 bg-blue-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-900">
                El sistema dice: {ESTADO_DE_CIERRE_LABEL[fila.estado]}
              </p>
              <p className="mt-1 text-sm text-blue-900">{AVISO_POR_ESTADO[fila.estado]}</p>
            </div>
          )}

          {/* --- Unidades por producto ------------------------------------ */}
          <section>
            <div className="mb-2 flex items-baseline justify-between border-b border-gray-100 pb-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Unidades por producto
              </h3>
              <span className="text-sm font-bold text-gray-900">
                {totalUnidades(form.ventas)} en total
              </span>
            </div>
            {lineas.map((linea) => (
              <div key={linea} className="mt-2">
                <p className="text-xs font-medium text-gray-500">{linea}</p>
                <div className="mt-1 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {skus.filter((s) => s.linea === linea).map((s) => {
                    const v = form.ventas.find((x) => x.sku_code === s.code);
                    return (
                      <label key={s.code} className="flex items-center gap-2 text-sm">
                        <span className="min-w-0 flex-1 truncate text-gray-700">{s.label}</span>
                        <input
                          type="number" min={0} step={1} inputMode="numeric"
                          value={v ? String(v.unidades) : '0'}
                          onChange={(e) => setUnidades(s.code, e.target.value)}
                          className="w-20 rounded border border-gray-300 px-2 py-1 text-right"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            {/* Cero unidades es una respuesta válida: hay turnos en que no se vende nada, y forzar
                un número mayor que cero es pedirle a alguien que invente. */}
            <p className="mt-2 text-xs text-gray-500">
              Cero es una respuesta válida. Deja en 0 lo que no se vendió.
            </p>
          </section>

          {/* --- Ejecución en tienda -------------------------------------- */}
          <Seccion titulo="Ejecución en tienda">
            <Selector
              etiqueta="¿Faltó producto en góndola?" valor={form.quiebre} opciones={QUIEBRE}
              onChange={(v) => set('quiebre', v)}
            />
            {form.quiebre === 'algunos' && (
              <div className="md:col-span-2">
                <p className="mb-1 text-xs text-gray-500">¿Cuáles faltaron?</p>
                <div className="flex flex-wrap gap-2">
                  {skus.map((s) => (
                    <label key={s.code} className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs">
                      <input
                        type="checkbox" checked={form.quiebre_skus.includes(s.code)}
                        onChange={(e) => set('quiebre_skus', e.target.checked
                          ? [...form.quiebre_skus, s.code]
                          : form.quiebre_skus.filter((c) => c !== s.code))}
                      />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Selector
              etiqueta="¿Hubo exhibición además de la góndola?" valor={form.exhibicion_adicional}
              opciones={EXHIBICION} onChange={(v) => set('exhibicion_adicional', v)}
            />
            <Texto
              etiqueta={esTakis ? cuestionario.precioPropio.label : "Precio de nuestro producto (S/)"} valor={form.precio_referencia}
              onChange={(v) => set('precio_referencia', v)} placeholder={esTakis ? "Precio en tienda" : "6.90"}
            />
            {!esTakis && (
            <Area
              etiqueta="Comentario de ejecución" valor={form.ejecucion_comentario}
              onChange={(v) => set('ejecucion_comentario', v)}
            />
            )}
          </Seccion>

          {esTakis && <TakisEjecucion valor={form.ejecucion_comentario}
            onChange={(v) => set('ejecucion_comentario', v)} />}

          {/* --- Competencia ---------------------------------------------- */}
          <Seccion titulo="Competencia">
            <Selector
              etiqueta="¿Había impulsadora de otra marca?" valor={form.competencia_impulsadora}
              opciones={competencia} onChange={(v) => set('competencia_impulsadora', v)}
            />
            {form.competencia_impulsadora === 'otra' && (
              <Texto
                etiqueta="¿De qué marca?" valor={form.competencia_cual}
                onChange={(v) => set('competencia_cual', v)}
              />
            )}
            <Texto
              etiqueta="Exhibición de la competencia" valor={form.competencia_exhibicion}
              onChange={(v) => set('competencia_exhibicion', v)}
            />
            <Texto
              etiqueta={esTakis ? cuestionario.precioCompetencia.label : "Precio de la competencia (S/)"} valor={form.competencia_precio}
              onChange={(v) => set('competencia_precio', v)} placeholder="5.50"
            />
            <Area
              etiqueta="Comentario de competencia" valor={form.competencia_comentario}
              onChange={(v) => set('competencia_comentario', v)}
            />
          </Seccion>

          {/* --- Objeciones ----------------------------------------------- */}
          <section>
            <h3 className="mb-2 border-b border-gray-100 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
              ¿Qué escuchó de los clientes?
            </h3>
            <div className="space-y-2">
              {objeciones.map((o) => {
                const elegida = form.objeciones.find((x) => x.valor === o.valor);
                return (
                  <div key={o.valor}>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox" checked={!!elegida}
                        onChange={() => marcarObjecion(o.valor)}
                      />
                      {o.etiqueta}
                    </label>
                    {/* La pregunta propia de cada objeción, que es el pedido del usuario:
                        «solamente pone el precio, pero ¿de qué?». */}
                    {elegida && o.pregunta !== '' && (
                      <input
                        type="text" value={elegida.comentario} placeholder={o.pregunta}
                        onChange={(e) => comentarioObjecion(o.valor, e.target.value)}
                        className="mt-1 ml-6 w-[calc(100%-1.5rem)] rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* --- Incidencias ---------------------------------------------- */}
          <Seccion titulo="¿Algo impidió trabajar normal?">
            <Selector
              etiqueta="Incidencia" valor={form.incidencia} opciones={INCIDENCIA}
              onChange={(v) => set('incidencia', v)}
            />
            {form.incidencia !== 'ninguna' && (
              <Area
                etiqueta="¿Qué pasó?" valor={form.incidencia_detalle}
                onChange={(v) => set('incidencia_detalle', v)}
              />
            )}
          </Seccion>

          {/* --- El motivo, que es obligatorio ---------------------------- */}
          <section className="rounded border border-amber-200 bg-amber-50 p-3">
            <label className="block text-xs font-semibold uppercase tracking-wide text-amber-900">
              {editando ? 'Motivo de la corrección' : 'Motivo del registro a mano'}
            </label>
            <p className="mt-1 text-xs text-amber-800">
              {editando
                ? 'Queda en el historial del cierre, campo por campo, con tu nombre y la hora.'
                : 'Este cierre va a quedar marcado como completado por la oficina, no por el campo. '
                  + 'El motivo es lo único que explica por qué.'}
            </p>
            <input
              type="text" value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Se quedó sin batería y lo pasó por WhatsApp"
              className="mt-2 w-full rounded border border-amber-300 px-2 py-1 text-sm"
            />
          </section>

          {editando && sinCambios && (
            <p className="text-sm text-gray-500">
              Todavía no cambiaste ningún dato: solo viaja lo que difiere de lo guardado.
            </p>
          )}
          {error !== '' && (
            <p role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            type="button" onClick={onCerrar}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button" onClick={() => void guardar()} disabled={guardando}
            className="rounded bg-blue-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : editando ? 'Guardar la corrección' : 'Registrar el cierre'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Piezas de formulario. Chicas y sin estado: el estado vive en el modal.     */
/* -------------------------------------------------------------------------- */

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 border-b border-gray-100 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {titulo}
      </h3>
      <div className="grid gap-3 md:grid-cols-2">{children}</div>
    </section>
  );
}

function Selector({ etiqueta, valor, opciones, onChange }: {
  etiqueta: string;
  valor: string;
  opciones: readonly { valor: string; etiqueta: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="block text-xs text-gray-500">{etiqueta}</span>
      <select
        value={valor} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
      >
        {opciones.map((o) => <option key={o.valor} value={o.valor}>{o.etiqueta}</option>)}
      </select>
    </label>
  );
}

function Texto({ etiqueta, valor, onChange, placeholder }: {
  etiqueta: string; valor: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <label className="block text-sm">
      <span className="block text-xs text-gray-500">{etiqueta}</span>
      <input
        type="text" value={valor} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
      />
    </label>
  );
}

function Area({ etiqueta, valor, onChange }: {
  etiqueta: string; valor: string; onChange: (v: string) => void;
}) {
  return (
    <label className="block text-sm md:col-span-2">
      <span className="block text-xs text-gray-500">{etiqueta}</span>
      <textarea
        rows={2} value={valor} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
      />
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Hidratación y diff                                                         */
/* -------------------------------------------------------------------------- */

/** El texto de un número que puede no existir. `null` no es «0»: es «no se preguntó». */
const num = (v: number | null): string => (v === null ? '' : String(v));

/**
 * Arma el estado inicial del formulario.
 *
 * En alta son los valores por defecto del cuestionario. En edición es EXACTAMENTE lo guardado,
 * porque de esa igualdad depende que el diff detecte solo lo que la persona tocó.
 *
 * Las ventas se arman recorriendo el CATÁLOGO y no las líneas guardadas: un SKU que la impulsadora
 * dejó en cero no tiene fila en `impulso_report_lines`, y si el formulario solo mostrara lo
 * guardado, esos productos no se podrían corregir — que es justo lo que alguien viene a hacer.
 */
function hidratar(fila: FilaDeCierre, skus: SkuDeImpulso[]): CierreDeTurno {
  const c = fila.cierre;
  const ventas: VentaSku[] = skus.map((s) => ({
    sku_code: s.code,
    unidades: c?.porSku.find((x) => x.sku === s.code)?.unidades ?? 0,
  }));
  const objeciones: ObjecionElegida[] = (c?.objeciones ?? [])
    .map((o) => ({ valor: o.valor, comentario: o.comentario ?? '' }));
  return {
    campaign_id: c?.campaignId ?? '',
    pdv_id: c?.pdvId ?? '',
    assignment_id: fila.assignmentId,
    fecha: fila.fecha,
    ventas,
    quiebre: c?.quiebre ?? 'ninguno',
    quiebre_skus: c?.quiebreSkus ?? [],
    exhibicion_adicional: c?.exhibicion ?? 'no',
    precio_referencia: num(c?.precioReferencia ?? null),
    ejecucion_comentario: c?.ejecucionComentario ?? '',
    competencia_impulsadora: c?.competencia ?? 'no',
    competencia_cual: c?.competenciaCual ?? '',
    competencia_exhibicion: c?.competenciaExhibicion ?? '',
    competencia_precio: num(c?.competenciaPrecio ?? null),
    competencia_comentario: c?.competenciaComentario ?? '',
    objeciones,
    incidencia: c?.incidencia ?? 'ninguna',
    incidencia_detalle: c?.incidenciaDetalle ?? '',
    espacio: '',
    mercaderista: '',
    premios_entregados: '',
    premios_quedan: '',
    // La oficina todavía no edita el merch; se construyen vacíos para no inventar conteos
    // que nadie hizo. Mostrarlos acá es lo que queda pendiente del panel.
    shaker_entregados: '',
    porta_entregados: '',
    vaso_entregados: '',
    toalla_entregados: '',
    ventilador_entregados: '',
    shaker_quedan: '',
    porta_quedan: '',
    vaso_quedan: '',
    toalla_quedan: '',
    ventilador_quedan: '',
    merch_foto: '',
  };
}

/**
 * Qué cambió de verdad. Solo eso viaja.
 *
 * `loQueFaltaEnCierre` ya validó el formulario, así que acá no se valida: se compara. Las ventas
 * viajan enteras cuando alguna unidad cambió —la RPC las recorre y salta las que quedaron
 * iguales—, y las objeciones también, porque son un conjunto y lo que cambia es el conjunto.
 */
export function diff(antes: CierreDeTurno, ahora: CierreDeTurno): CambiosDeCierre {
  const d: CambiosDeCierre = {};
  const simples: (keyof CambiosDeCierre & keyof CierreDeTurno)[] = [
    'quiebre', 'exhibicion_adicional', 'precio_referencia', 'ejecucion_comentario',
    'competencia_impulsadora', 'competencia_cual', 'competencia_exhibicion',
    'competencia_precio', 'competencia_comentario', 'incidencia', 'incidencia_detalle',
  ];
  for (const k of simples) {
    const a = antes[k];
    const b = ahora[k];
    if (typeof a === 'string' && typeof b === 'string' && a.trim() !== b.trim()) {
      (d as Record<string, unknown>)[k] = b.trim();
    }
  }
  if ([...antes.quiebre_skus].sort().join('|') !== [...ahora.quiebre_skus].sort().join('|')) {
    d.quiebre_skus = ahora.quiebre_skus;
  }
  const clave = (v: VentaSku[]) => v.map((x) => `${x.sku_code}=${x.unidades}`).sort().join('|');
  if (clave(antes.ventas) !== clave(ahora.ventas)) {
    d.ventas = ahora.ventas.map((v) => ({ sku_code: v.sku_code, unidades: v.unidades }));
  }
  const claveObj = (o: ObjecionElegida[]) =>
    o.map((x) => `${x.valor}=${x.comentario.trim()}`).sort().join('|');
  if (claveObj(antes.objeciones) !== claveObj(ahora.objeciones)) {
    d.objeciones = ahora.objeciones
      .filter((o) => o.valor !== OBJECION_NINGUNA)
      .map((o) => ({ objecion: o.valor, comentario: o.comentario.trim() }));
  }
  return d;
}
